import { useCallback, useEffect, useRef } from 'react'
import type { ApolloError } from '@apollo/client'

interface UseSubscriptionRetryOptions {
  error: ApolloError | null | undefined
  maxRetries?: number
  restart: () => void
  retryDelay?: number
  subscriptionName?: string
}

/**
 * Custom hook that handles subscription error retry logic
 * Automatically retries subscriptions on errors (except validation and auth errors)
 * @param options Configuration for retry behavior
 */
export const useSubscriptionRetry = ({
  error,
  restart,
  maxRetries = 3,
  retryDelay = 2000,
  subscriptionName = 'Subscription',
}: UseSubscriptionRetryOptions) => {
  const retryCountRef = useRef(0)

  // Memoize restart to prevent infinite loops
  const memoizedRestart = useCallback(restart, [restart])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null

    if (error) {
      const isValidationError =
        error.message?.includes('Validation error') ||
        error.message?.includes('FieldUndefined')
      const isAuthError = error.message?.includes('Jwt expired')

      // Don't retry GraphQL validation errors or auth errors - they won't resolve themselves
      if (isValidationError || isAuthError) {
        const errorType = isValidationError ? 'validation' : 'authentication'
        // eslint-disable-next-line no-console
        console.error(
          `${subscriptionName} ${errorType} error - not retrying:`,
          error.message,
        )
      } else if (retryCountRef.current < maxRetries) {
        // eslint-disable-next-line no-console
        console.log(
          'Retrying:',
          retryCountRef.current,
          'of',
          maxRetries,
          'Error:',
          error?.message,
        )
        retryCountRef.current += 1

        // eslint-disable-next-line no-console
        console.error(
          `${subscriptionName} error, retrying (${retryCountRef.current}/${maxRetries})...`,
          error.message,
        )

        timeoutId = setTimeout(() => {
          memoizedRestart()
        }, retryDelay)
      } else {
        // eslint-disable-next-line no-console
        console.error(
          `${subscriptionName} failed after maximum retries`,
          error.message,
        )
      }
    } else {
      // Reset retry count on successful connection
      if (retryCountRef.current > 0) {
        // eslint-disable-next-line no-console
        console.log(
          'Subscription recovered, resetting retry count from',
          retryCountRef.current,
        )
      }
      retryCountRef.current = 0
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [error, memoizedRestart, maxRetries, retryDelay, subscriptionName])
}
